/* RescueScheduledMediaPanel — one-click "Rescue scheduled posts" action
 * for the Admin → Deploy tab.
 *
 * Context: prior to the media-blob self-heal feature, scheduled posts that
 * referenced images on disk would publish text-only (LinkedIn) or fail
 * outright (Facebook error 324) after a redeploy wiped the uploads
 * directory. This panel backfills the durable `media_blobs` collection
 * for any already-scheduled post that's still vulnerable, so admins can
 * "rescue" their existing queue without manually re-creating posts.
 *
 * Safe to run repeatedly — idempotent on the backend.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { LifeBuoy, Loader2, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

export const RescueScheduledMediaPanel = ({ api }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [showFailures, setShowFailures] = useState(false);

  const run = async () => {
    if (running) return;
    const ok = window.confirm(
      'Rescue scheduled posts now?\n\n' +
      'This will scan every scheduled post that has an image, download any image bytes ' +
      'that are missing from durable storage, and persist them to MongoDB so the file ' +
      'survives future redeploys.\n\n' +
      'Already-safe posts are skipped. Safe to run repeatedly.'
    );
    if (!ok) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post('/admin/rescue-scheduled-media');
      setResult(r.data);
      if (r.data?.rescued > 0) {
        toast.success(
          `Rescued ${r.data.rescued} image${r.data.rescued === 1 ? '' : 's'} — your scheduled queue is now safe across redeploys.`
        );
      } else if (r.data?.failed === 0) {
        toast.success('All scheduled posts were already safe — nothing to rescue.');
      } else {
        toast.warning(`${r.data?.failed} image(s) couldn't be recovered — review the failures list below.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rescue failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="border-zinc-200" data-testid="rescue-scheduled-media-panel">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <LifeBuoy className="w-4 h-4 text-indigo-600" />
          Rescue scheduled posts
        </CardTitle>
        <CardDescription className="text-xs">
          Scan every scheduled post for missing image bytes and back-fill the durable storage so
          the next redeploy doesn't strip images from your queue. Idempotent — safe to run anytime.
          Most useful right after deploying the media-blob self-heal feature, or after any pod
          incident that wiped <code className="text-[10px] bg-zinc-100 px-1 rounded">/app/backend/uploads/</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        <Button
          onClick={run}
          disabled={running}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="rescue-run-btn"
        >
          {running ? (
            <>
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              Scanning…
            </>
          ) : (
            <>
              <LifeBuoy className="w-3.5 h-3.5 mr-1.5" />
              Run rescue scan
            </>
          )}
        </Button>

        {result && (
          <div
            className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 space-y-3"
            data-testid="rescue-result"
          >
            <div className="flex flex-wrap items-center gap-2">
              {result.rescued > 0 ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              ) : result.failed > 0 ? (
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              ) : (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              )}
              <span className="text-sm font-medium text-zinc-900">
                Scanned {result.posts_scanned} scheduled posts ({result.images_total} images) in{' '}
                {(result.duration_ms / 1000).toFixed(1)}s
              </span>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Rescued: {result.rescued}
              </Badge>
              <Badge className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100">
                Already safe: {result.already_safe}
              </Badge>
              {result.failed > 0 && (
                <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Failed: {result.failed}
                </Badge>
              )}
            </div>
            {result.failures?.length > 0 && (
              <div>
                <button
                  type="button"
                  onClick={() => setShowFailures((s) => !s)}
                  className="text-xs text-rose-700 hover:underline inline-flex items-center gap-1"
                  data-testid="rescue-toggle-failures"
                >
                  {showFailures ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                  {showFailures ? 'Hide' : 'Show'} {result.failures.length} failure
                  {result.failures.length === 1 ? '' : 's'}
                  {result.failures.length === 20 && result.failed > 20 && ' (first 20)'}
                </button>
                {showFailures && (
                  <ul className="mt-2 space-y-1.5 text-xs max-h-64 overflow-auto">
                    {result.failures.map((f, i) => (
                      <li
                        key={i}
                        className="rounded border border-rose-200 bg-white p-2"
                        data-testid={`rescue-failure-${i}`}
                      >
                        <div className="font-mono text-[11px] text-zinc-700 truncate" title={f.filename}>
                          {f.filename}
                        </div>
                        <div className="text-rose-700 mt-0.5 break-words">{f.error}</div>
                        <div className="text-[10px] text-zinc-500 mt-0.5">
                          Post: <span className="font-mono">{f.post_id}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[11px] text-zinc-500 mt-2">
                  For these posts: the image bytes can't be recovered. Either the public URL is no
                  longer serving the file, or the post is on a different host. Delete and recreate
                  the post (with the original image) to ensure the next publish has media.
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
